import {
  Appointment,
  ScribeListClinicSignReqValue,
} from "../models/admissions.model";
import {
  readClinicalStatusField,
  readLegacyEncounterStatusField,
} from "./encounter-clinical-status.read.util";
import { resolveEncounterScheduleStatus } from "./encounter-schedule-status.util";
import { EncounterStatusV2 } from "../services/utils";

export type ScribeSignReqStatus =
  | "Signed"
  | "Unsigned"
  | "Partially Signed"
  | "Not Required";

/** UI filter options that map to list-clinic `signReq`. */
export const SCRIBE_SIGN_REQ_FILTER_OPTIONS: ScribeSignReqStatus[] = [
  "Signed",
  "Unsigned",
  "Not Required",
];

/** Map Sign Req UI status → list-clinic `signReq` body value. */
export function scribeSignReqStatusToApi(
  status: ScribeSignReqStatus | string | null | undefined
): ScribeListClinicSignReqValue | null {
  switch ((status ?? "").trim()) {
    case "Signed":
      return "signed";
    case "Unsigned":
      return "unsigned";
    case "Not Required":
      return "null";
    default:
      return null;
  }
}

/** Map list-clinic `signReq` value → Sign Req UI label. */
export function scribeSignReqApiToStatus(
  value: string | null | undefined
): ScribeSignReqStatus {
  switch ((value ?? "").trim().toLowerCase()) {
    case "signed":
      return "Signed";
    case "unsigned":
      return "Unsigned";
    case "null":
      return "Not Required";
    default:
      return "Not Required";
  }
}

function pickBooleanField(
  raw: Record<string, unknown>,
  keys: string[]
): boolean | null {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === "boolean") {
      return value;
    }
    if (value === "true" || value === 1 || value === "1") {
      return true;
    }
    if (value === "false" || value === 0 || value === "0") {
      return false;
    }
  }
  return null;
}

function readSoapNoteSignableFromRow(
  raw: Record<string, unknown>
): Record<string, unknown> | null {
  const signables = raw["signables"];
  if (!Array.isArray(signables)) {
    return null;
  }
  return (
    (signables as Record<string, unknown>[]).find((signable) => {
      const type = String(signable?.signableType ?? signable?.type ?? "")
        .trim()
        .toLowerCase();
      return type === "soap_note" || type === "soap note";
    }) ?? null
  );
}

/**
 * Signed / Unsigned from the encounter's soap_note signable:
 * requiresProviderSignature + providerSigned on that signable row.
 */
function resolveSoapNoteProviderSignatureStatus(
  soapSignable: Record<string, unknown> | null
): ScribeSignReqStatus | null {
  if (!soapSignable) {
    return null;
  }

  const requiresProviderSignature = pickBooleanField(soapSignable, [
    "requiresProviderSignature",
    "requires_provider_signature",
  ]);
  const providerSigned = pickBooleanField(soapSignable, [
    "providerSigned",
    "provider_signed",
  ]);

  if (requiresProviderSignature !== true) {
    return null;
  }

  if (providerSigned === true) {
    return "Signed";
  }

  if (providerSigned === false) {
    return "Unsigned";
  }

  return null;
}

function isScribeSignatureRequired(appt: Appointment): boolean {
  const scheduleStatus = resolveEncounterScheduleStatus(appt);
  if (scheduleStatus === EncounterStatusV2.SignReq) {
    return true;
  }
  const clinical =
    readClinicalStatusField(appt) || readLegacyEncounterStatusField(appt);
  return clinical === "Sign Req";
}

/** Scribe Sign Req column label from list-clinic row fields. */
export function resolveScribeSignReqStatus(
  appt: Appointment
): ScribeSignReqStatus {
  const raw = appt as unknown as Record<string, unknown>;
  const soapSignable = readSoapNoteSignableFromRow(raw);
  const fromSoapNote = resolveSoapNoteProviderSignatureStatus(soapSignable);

  if (fromSoapNote != null) {
    return fromSoapNote;
  }

  if (isScribeSignatureRequired(appt)) {
    return "Unsigned";
  }

  return "Not Required";
}

/** Scribe list only shows Signed, Unsigned, and Partially Signed — not "Not Required". */
export function isScribeSignReqDisplayable(status: ScribeSignReqStatus): boolean {
  return status !== "Not Required";
}

export function resolveScribeSignReqDisplayStatus(
  appt: Appointment
): ScribeSignReqStatus | null {
  const status = resolveScribeSignReqStatus(appt);
  return isScribeSignReqDisplayable(status) ? status : null;
}

/** Bulk Sign All: row needs a provider signature on the SOAP note signable. */
export function isScribeEncounterEligibleForBulkSign(
  appt: Appointment
): boolean {
  const status = resolveScribeSignReqStatus(appt);
  return status === "Unsigned" || status === "Partially Signed";
}

/** Bootstrap soft badge class for the Scribe Sign Req column. */
export function getScribeSignReqBadgeClass(status: ScribeSignReqStatus): string {
  switch (status) {
    case "Signed":
      return "badge-soft-success";
    case "Unsigned":
      return "badge-soft-warning";
    case "Partially Signed":
      return "badge-soft-info";
    case "Not Required":
    default:
      return "badge-soft-secondary";
  }
}
